import React from "react";

import { useDatasetMappedValue } from "hooks/useDatasetMappedValue";
import { DatasetDataInfo } from "models/dataset-models";
import { fmtDatetime, formatDuration, getDiffSecondsInstant, toInstant } from "utils/time-utils";
import { formatDatasetValue } from "utils/value-format-utils";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { I, T } from "services/lang/LanguageService";
import { useDatasetMappedIcon } from "hooks/useDatasetMappedIcon";
import { useDisplayTimezone } from "hooks/useTimezone";
import { DATE_PATTERN_SECONDS } from "utils/constants";
import { Instant } from "@js-joda/core";

type DatasetTooltipProps = {
  data: InstantDoubleValue | null | undefined;
  time: Instant;
  dataset: DatasetDataInfo;
  hide?: boolean;
};

function DatasetTooltipText({ data, time, dataset, hide = false }: DatasetTooltipProps) {
  const displayTz = useDisplayTimezone();
  if (hide) {
    return null;
  }

  const fmtTime = fmtDatetime(time, displayTz, DATE_PATTERN_SECONDS);
  const noData = !data!;

  let tagValueAge: React.ReactNode = null;

  if (dataset?.loadedTime && time) {
    const loaded = toInstant(dataset.loadedTime);
    const diffSec = getDiffSecondsInstant(loaded, time);
    const duration = formatDuration(diffSec);

    if (loaded.isAfter(time)) {
      tagValueAge = (
        <>
          ({T("word.age")}: {duration})
        </>
      );
    } else {
      tagValueAge = (
        <>
          ({T("word.age")}: - {duration})
        </>
      );
    }
  }

  return (
    <div style={{ whiteSpace: "pre" }}>
      <div>
        <b>{I(dataset?.title)}:</b>{" "}
        {dataset.stringValueMapper ? (
          <MappedValue mapValue={data?.value} mapperId={dataset.stringValueMapper} isTooltip />
        ) : (
          <i>
            {formatDatasetValue(dataset, data)}
            {data?.value && ` ${dataset?.unit}`}
          </i>
        )}
      </div>
      <div>
        <b>{T("word.mesurement-date")}:</b> <i>{fmtTime || T("word.no-data")}</i>{" "}
        <i>{!noData ? tagValueAge : ""}</i>
      </div>
    </div>
  );
}

export default DatasetTooltipText;

type MappedValueProps = {
  mapValue: number | undefined;
  mapperId: number;
  isTooltip?: boolean;
};

export const MappedValue: React.FC<MappedValueProps> = ({ mapValue, mapperId, isTooltip }) => {
  const initialMappedValue = useDatasetMappedValue(mapValue, mapperId);
  const mappedValue = typeof initialMappedValue === "string" ? I(initialMappedValue) : initialMappedValue;
  const mappedIcon = useDatasetMappedIcon(mapValue, mapperId);

  if (isTooltip) {
    return (
      <i>{mappedValue ? `${mapValue} - ${mappedValue}` : T("message.no-mapper-value-for", { mapValue })}</i>
    );
  }

  if (mappedIcon) {
    return <i className={mappedIcon as string} />;
  }
  return <i>{mappedValue ?? T("message.no-mapper-value-for", { mapValue })}</i>;
};
